import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Package, ShoppingCart, Upload, Plus, Trash2, Edit2, Check, X,
  DollarSign, Truck, Eye, Download, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    paid: 'bg-green-500/20 text-green-400 border-green-500/30',
    shipped: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    delivered: 'bg-green-500/20 text-green-400 border-green-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <Badge className={`${styles[status] || 'bg-gray-500/20 text-gray-400'} border text-xs`}>
      {status}
    </Badge>
  );
};

const AdminDongleShop = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [releases, setReleases] = useState([]);
  
  // Form states
  const [showProductForm, setShowProductForm] = useState(false);
  const [showReleaseForm, setShowReleaseForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    short_description: '',
    price: '',
    compare_at_price: '',
    device_type: 'ELM327',
    image_url: '',
    is_featured: true
  });
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [releaseForm, setReleaseForm] = useState({
    version: '',
    version_code: '',
    release_notes: '',
    update_policy: 'optional'
  });
  const [releaseFile, setReleaseFile] = useState(null);

  // Fetch data
  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dongle-shop/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [token]);

  const fetchPurchases = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPurchases(data.purchases || []);
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  }, [token]);

  const fetchReleases = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/live-obd/releases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReleases(data.releases || []);
      }
    } catch (error) {
      console.error('Error fetching releases:', error);
    }
  }, [token]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchProducts(), fetchPurchases(), fetchReleases()]);
    setLoading(false);
  }, [fetchProducts, fetchPurchases, fetchReleases]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handle product image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProductImageFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setProductImagePreview(previewUrl);
    }
  };

  // Upload product image
  const uploadProductImage = async () => {
    if (!productImageFile) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', productImageFile);
      
      const response = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/products/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.image_url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Fehler beim Bildupload');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Product CRUD
  const saveProduct = async () => {
    try {
      // Upload image first if selected
      let imageUrl = productForm.image_url;
      if (productImageFile) {
        const uploadedUrl = await uploadProductImage();
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }
      
      const url = editingProduct 
        ? `${BACKEND_URL}/api/dongle-shop/admin/products/${editingProduct.id}`
        : `${BACKEND_URL}/api/dongle-shop/admin/products`;
      
      const method = editingProduct ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...productForm,
          image_url: imageUrl,
          price: parseFloat(productForm.price) || 0,
          compare_at_price: productForm.compare_at_price ? parseFloat(productForm.compare_at_price) : null
        })
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Produkt aktualisiert' : 'Produkt erstellt');
        setShowProductForm(false);
        setEditingProduct(null);
        setProductForm({
          name: '', description: '', short_description: '', price: '',
          compare_at_price: '', device_type: 'ELM327', image_url: '', is_featured: true
        });
        setProductImageFile(null);
        setProductImagePreview(null);
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Produkt gelöscht');
        fetchProducts();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      short_description: product.short_description || '',
      price: product.price.toString(),
      compare_at_price: product.compare_at_price?.toString() || '',
      device_type: product.device_type,
      image_url: product.image_url || '',
      is_featured: product.is_featured
    });
    setShowProductForm(true);
  };

  // Purchase management
  const updatePurchaseStatus = async (purchaseId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/purchases/${purchaseId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status auf "${newStatus}" geändert`);
        fetchPurchases();
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Release upload
  const uploadRelease = async () => {
    if (!releaseFile || !releaseForm.version || !releaseForm.version_code) {
      toast.error('Bitte alle Felder ausfüllen und eine Datei auswählen');
      return;
    }

    const formData = new FormData();
    formData.append('file', releaseFile);
    formData.append('version', releaseForm.version);
    formData.append('version_code', parseInt(releaseForm.version_code, 10).toString());
    formData.append('release_notes', releaseForm.release_notes || 'New release');
    formData.append('update_policy', releaseForm.update_policy);

    try {
      toast.loading('Uploading release...', { id: 'upload' });
      const response = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/releases/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        toast.success('Release erfolgreich hochgeladen', { id: 'upload' });
        setShowReleaseForm(false);
        setReleaseForm({ version: '', version_code: '', release_notes: '', update_policy: 'optional' });
        setReleaseFile(null);
        fetchReleases();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Upload', { id: 'upload' });
        console.error('Upload failed:', error);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Netzwerkfehler beim Upload', { id: 'upload' });
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-green-400" />
            Dongle Shop & Agent Releases
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Verwalten Sie Dongle-Produkte, Bestellungen und Agent-Software
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAllData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#1a2332] border border-gray-800">
          <TabsTrigger value="products" className="data-[state=active]:bg-blue-600">
            <Package className="w-4 h-4 mr-2" />
            Produkte ({products.length})
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-blue-600">
            <Truck className="w-4 h-4 mr-2" />
            Bestellungen ({purchases.length})
          </TabsTrigger>
          <TabsTrigger value="releases" className="data-[state=active]:bg-blue-600">
            <Upload className="w-4 h-4 mr-2" />
            Agent Releases ({releases.length})
          </TabsTrigger>
          <TabsTrigger value="github" className="data-[state=active]:bg-blue-600">
            <Download className="w-4 h-4 mr-2" />
            GitHub Auto-Update
          </TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Dongle Produkte</CardTitle>
                <Button onClick={() => { setEditingProduct(null); setShowProductForm(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Neues Produkt
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Product Form */}
              {showProductForm && (
                <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <h3 className="text-white font-medium mb-4">
                    {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-sm">Name *</label>
                      <Input
                        value={productForm.name}
                        onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="z.B. CarCortex OBD Pro"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Gerätetyp</label>
                      <Input
                        value={productForm.device_type}
                        onChange={(e) => setProductForm({...productForm, device_type: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="z.B. ELM327"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Preis (CHF) *</label>
                      <Input
                        type="number"
                        value={productForm.price}
                        onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="49.99"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Vergleichspreis CHF (optional)</label>
                      <Input
                        type="number"
                        value={productForm.compare_at_price}
                        onChange={(e) => setProductForm({...productForm, compare_at_price: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="69.99"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-gray-400 text-sm">Kurzbeschreibung</label>
                      <Input
                        value={productForm.short_description}
                        onChange={(e) => setProductForm({...productForm, short_description: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="Kompakter OBD2-Adapter mit Bluetooth"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-gray-400 text-sm">Beschreibung</label>
                      <textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        className="w-full bg-gray-900 border border-gray-700 rounded-md p-2 text-white"
                        rows={3}
                        placeholder="Ausführliche Produktbeschreibung..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-gray-400 text-sm mb-2 block">Produktbild</label>
                      <div className="flex items-start gap-4">
                        {/* Image Preview */}
                        <div className="w-32 h-32 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center overflow-hidden">
                          {productImagePreview ? (
                            <img src={productImagePreview} alt="Preview" className="w-full h-full object-cover" />
                          ) : productForm.image_url ? (
                            <img 
                              src={productForm.image_url.startsWith('/api') ? `${BACKEND_URL}${productForm.image_url}` : productForm.image_url} 
                              alt="Current" 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <Package className="w-12 h-12 text-gray-600" />
                          )}
                        </div>
                        
                        {/* Upload Controls */}
                        <div className="flex-1 space-y-3">
                          {/* File Upload */}
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                className="hidden"
                              />
                              <div className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md text-sm font-medium flex items-center gap-2 transition-colors">
                                <Upload className="w-4 h-4" />
                                {productImageFile ? 'Anderes Bild wählen' : 'Bild hochladen'}
                              </div>
                            </label>
                            {productImageFile && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setProductImageFile(null);
                                  setProductImagePreview(null);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          
                          {productImageFile && (
                            <p className="text-xs text-green-400">
                              ✓ {productImageFile.name} ausgewählt ({(productImageFile.size / 1024).toFixed(1)} KB)
                            </p>
                          )}
                          
                          {/* Or URL input */}
                          <div className="text-gray-500 text-xs flex items-center gap-2">
                            <span className="border-t border-gray-700 flex-1"></span>
                            <span>ODER URL eingeben</span>
                            <span className="border-t border-gray-700 flex-1"></span>
                          </div>
                          
                          <Input
                            value={productForm.image_url}
                            onChange={(e) => {
                              setProductForm({...productForm, image_url: e.target.value});
                              // Clear file if URL is entered
                              if (e.target.value) {
                                setProductImageFile(null);
                                setProductImagePreview(null);
                              }
                            }}
                            className="bg-gray-900 border-gray-700 text-sm"
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>
                      </div>
                      
                      {uploadingImage && (
                        <div className="mt-2 flex items-center gap-2 text-cyan-400">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Bild wird hochgeladen...</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={productForm.is_featured}
                        onChange={(e) => setProductForm({...productForm, is_featured: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <label className="text-gray-400 text-sm">Als Upsell anzeigen (Featured)</label>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={saveProduct} className="bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-2" />
                      Speichern
                    </Button>
                    <Button variant="outline" onClick={() => setShowProductForm(false)}>
                      <X className="w-4 h-4 mr-2" />
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}

              {/* Products List */}
              {products.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Produkte vorhanden</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {products.map(product => (
                    <div key={product.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-4">
                        {product.image_url ? (
                          <img 
                            src={product.image_url.startsWith('/api') ? `${BACKEND_URL}${product.image_url}` : product.image_url} 
                            alt={product.name} 
                            className="w-16 h-16 object-cover rounded" 
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-500" />
                          </div>
                        )}
                        <div>
                          <h3 className="text-white font-medium">{product.name}</h3>
                          <p className="text-gray-400 text-sm">{product.device_type}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-green-400 font-bold">{product.price} CHF</span>
                            {product.compare_at_price && (
                              <span className="text-gray-500 line-through text-sm">{product.compare_at_price} CHF</span>
                            )}
                            {product.is_featured && (
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Featured</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => editProduct(product)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-400" onClick={() => deleteProduct(product.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">Bestellungen</CardTitle>
            </CardHeader>
            <CardContent>
              {purchases.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Bestellungen vorhanden</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Kunde</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Produkt</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Betrag</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Status</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Datum</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.map(purchase => (
                        <tr key={purchase.id} className="border-b border-gray-800/50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="text-white text-sm">{purchase.user_name || '-'}</p>
                              <p className="text-gray-500 text-xs">{purchase.user_email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-white text-sm">{purchase.product_name}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-green-400 font-medium">{purchase.total_price}€</span>
                          </td>
                          <td className="py-3 px-4">
                            <StatusBadge status={purchase.status} />
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-gray-400 text-sm">{formatDate(purchase.purchased_at)}</span>
                          </td>
                          <td className="py-3 px-4">
                            <select
                              value={purchase.status}
                              onChange={(e) => updatePurchaseStatus(purchase.id, e.target.value)}
                              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm"
                            >
                              <option value="pending">Ausstehend</option>
                              <option value="paid">Bezahlt</option>
                              <option value="shipped">Versendet</option>
                              <option value="delivered">Geliefert</option>
                              <option value="cancelled">Storniert</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Releases Tab */}
        <TabsContent value="releases" className="mt-4">
          <Card className="bg-[#1a2332] border-gray-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Agent Software Releases</CardTitle>
                <Button onClick={() => setShowReleaseForm(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Neues Release
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Release Upload Form */}
              {showReleaseForm && (
                <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <h3 className="text-white font-medium mb-4">Neues Release hochladen</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-400 text-sm">Version *</label>
                      <Input
                        value={releaseForm.version}
                        onChange={(e) => setReleaseForm({...releaseForm, version: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="1.0.1"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Version Code *</label>
                      <Input
                        type="number"
                        value={releaseForm.version_code}
                        onChange={(e) => setReleaseForm({...releaseForm, version_code: e.target.value})}
                        className="bg-gray-900 border-gray-700"
                        placeholder="101"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Update Policy</label>
                      <select
                        value={releaseForm.update_policy}
                        onChange={(e) => setReleaseForm({...releaseForm, update_policy: e.target.value})}
                        className="w-full bg-gray-900 border border-gray-700 rounded-md p-2 text-white"
                      >
                        <option value="optional">Optional</option>
                        <option value="recommended">Empfohlen</option>
                        <option value="mandatory">Pflicht</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-gray-400 text-sm">Datei (.exe, .msi, .zip) *</label>
                      <Input
                        type="file"
                        accept=".exe,.msi,.zip"
                        onChange={(e) => setReleaseFile(e.target.files[0])}
                        className="bg-gray-900 border-gray-700"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-gray-400 text-sm">Release Notes</label>
                      <textarea
                        value={releaseForm.release_notes}
                        onChange={(e) => setReleaseForm({...releaseForm, release_notes: e.target.value})}
                        className="w-full bg-gray-900 border border-gray-700 rounded-md p-2 text-white"
                        rows={3}
                        placeholder="Änderungen in dieser Version..."
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={uploadRelease} className="bg-green-600 hover:bg-green-700">
                      <Upload className="w-4 h-4 mr-2" />
                      Hochladen
                    </Button>
                    <Button variant="outline" onClick={() => setShowReleaseForm(false)}>
                      <X className="w-4 h-4 mr-2" />
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}

              {/* Releases List */}
              {releases.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Download className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Releases vorhanden</p>
                  <p className="text-sm">Laden Sie das erste Agent-Release hoch</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {releases.map(release => (
                    <div key={release.id} className="p-4 bg-gray-800/50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold">Version {release.version}</h3>
                            {release.is_latest && (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Aktuell</Badge>
                            )}
                            <Badge className={`
                              ${release.update_policy === 'mandatory' ? 'bg-red-500/20 text-red-400' : ''}
                              ${release.update_policy === 'recommended' ? 'bg-yellow-500/20 text-yellow-400' : ''}
                              ${release.update_policy === 'optional' ? 'bg-gray-500/20 text-gray-400' : ''}
                            `}>
                              {release.update_policy}
                            </Badge>
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{release.release_notes}</p>
                          <p className="text-gray-500 text-xs mt-2">
                            Code: {release.version_code} • Downloads: {release.download_count || 0}
                          </p>
                        </div>
                        {release.download_url_windows && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`${BACKEND_URL}${release.download_url_windows}`, '_blank')}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GitHub Auto-Update Tab */}
        <TabsContent value="github" className="mt-4 space-y-4">
          <GitHubReleasesPanel token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDongleShop;

// ==================== GITHUB AUTO-UPDATE PANEL ====================

const GitHubReleasesPanel = ({ token }) => {
  const [config, setConfig] = useState({ repo: '', token: '' });
  const [savedConfig, setSavedConfig] = useState(null);
  const [latestRelease, setLatestRelease] = useState(null);
  const [policy, setPolicy] = useState({ force_update: false, min_supported_version: '1.0.0' });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [confRes, polRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dongle-shop/admin/github-releases/config`, { headers }),
        fetch(`${BACKEND_URL}/api/dongle-shop/admin/agent-update-policy`, { headers }),
      ]);
      if (confRes.ok) {
        const c = await confRes.json();
        setSavedConfig(c);
        setConfig({ repo: c.repo || '', token: '' });
      }
      if (polRes.ok) setPolicy(await polRes.json());
      const latRes = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/github-releases/latest`, { headers });
      if (latRes.ok) setLatestRelease(await latRes.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    if (!config.repo || !config.repo.includes('/')) {
      toast.error("Repo Format: owner/name (z.B. carcortex/main)");
      return;
    }
    setLoading(true);
    try {
      const body = { repo: config.repo };
      if (config.token) body.token = config.token;
      const res = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/github-releases/config`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success("GitHub-Konfiguration gespeichert");
        const data = await res.json();
        setLatestRelease(data);
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Speichern fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshLatest = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/github-releases/latest?force=true`, { headers });
      if (res.ok) {
        setLatestRelease(await res.json());
        toast.success("Aktualisiert");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const savePolicy = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dongle-shop/admin/agent-update-policy`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(policy),
      });
      if (res.ok) {
        toast.success("Update-Policy gespeichert");
        setPolicy(await res.json());
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Configuration */}
      <Card className="bg-[#1a2332] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-cyan-400" />
            GitHub Repository
          </CardTitle>
          <CardDescription>
            Der OBD Agent wird über GitHub Releases verteilt. Trage hier dein Repo ein, danach baut GitHub Actions
            bei jedem Push automatisch eine neue Version mit erhöhter Versionsnummer.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Repository (owner/name)</label>
            <Input
              placeholder="z.B. carcortex/main"
              value={config.repo}
              onChange={(e) => setConfig({ ...config, repo: e.target.value })}
              className="bg-[#0F141A] border-white/10 text-white"
              data-testid="github-repo-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">
              Personal Access Token <span className="text-gray-500">(optional, nur für Private Repos)</span>
            </label>
            <Input
              type="password"
              placeholder={savedConfig?.token_masked || "ghp_xxx..."}
              value={config.token}
              onChange={(e) => setConfig({ ...config, token: e.target.value })}
              className="bg-[#0F141A] border-white/10 text-white"
              data-testid="github-token-input"
            />
            {savedConfig?.token_masked && (
              <p className="text-[10px] text-gray-500 mt-1">Aktuell hinterlegt: {savedConfig.token_masked}</p>
            )}
          </div>
          <Button onClick={saveConfig} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="github-save-config">
            <Check className="w-4 h-4 mr-2" />
            Konfiguration speichern
          </Button>
        </CardContent>
      </Card>

      {/* Force-Update Policy */}
      <Card className="bg-[#1a2332] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Auto-Update Richtlinie</CardTitle>
          <CardDescription>
            Steuere, ob Updates verpflichtend sind. Bei "Pflicht-Update" wird beim User ein Banner mit
            erzwungenem Download angezeigt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="force_update"
              checked={!!policy.force_update}
              onChange={(e) => setPolicy({ ...policy, force_update: e.target.checked })}
              className="w-4 h-4"
              data-testid="force-update-checkbox"
            />
            <label htmlFor="force_update" className="text-white text-sm">
              Pflicht-Update für alle Agents erzwingen
            </label>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Minimum unterstützte Version</label>
            <Input
              placeholder="1.0.0"
              value={policy.min_supported_version || ''}
              onChange={(e) => setPolicy({ ...policy, min_supported_version: e.target.value })}
              className="bg-[#0F141A] border-white/10 text-white max-w-xs"
              data-testid="min-supported-version-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Ältere Agent-Versionen werden ebenfalls als Pflicht-Update markiert.
            </p>
          </div>
          <Button onClick={savePolicy} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-policy-btn">
            Policy speichern
          </Button>
        </CardContent>
      </Card>

      {/* Latest Release Display */}
      <Card className="bg-[#1a2332] border-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Aktuelle GitHub Version</CardTitle>
            <Button size="sm" variant="outline" onClick={refreshLatest} disabled={refreshing} className="border-white/20 text-white">
              <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!latestRelease ? (
            <p className="text-gray-400 text-sm">Lade...</p>
          ) : latestRelease.error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
              <p className="text-red-300 text-sm">❌ {latestRelease.error}</p>
              {latestRelease.repo && <p className="text-xs text-gray-500 mt-1">Repo: {latestRelease.repo}</p>}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-2xl font-bold text-white">{latestRelease.version || '?'}</span>
                {latestRelease.tag_name && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    {latestRelease.tag_name}
                  </Badge>
                )}
                {latestRelease.published_at && (
                  <span className="text-xs text-gray-400">
                    {new Date(latestRelease.published_at).toLocaleString('de-DE')}
                  </span>
                )}
              </div>
              {latestRelease.release_notes && (
                <pre className="text-xs text-gray-300 whitespace-pre-wrap bg-black/30 p-3 rounded max-h-40 overflow-y-auto">
                  {latestRelease.release_notes}
                </pre>
              )}
              <div className="grid sm:grid-cols-3 gap-2">
                {['windows', 'macos', 'linux'].map(p => {
                  const url = latestRelease[`download_url_${p}`];
                  return (
                    <a
                      key={p}
                      href={url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 rounded border text-center text-sm ${url ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/20' : 'bg-gray-500/10 border-gray-500/20 text-gray-500 pointer-events-none'}`}
                      data-testid={`download-${p}-btn`}
                    >
                      <Download className="w-3 h-3 inline mr-1" />
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </a>
                  );
                })}
              </div>
              {latestRelease.html_url && (
                <a href={latestRelease.html_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300">
                  Auf GitHub ansehen →
                </a>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Setup help */}
      <Card className="bg-[#1a2332] border-cyan-500/30">
        <CardHeader>
          <CardTitle className="text-cyan-300 text-sm">Wie funktioniert das Auto-Update-System?</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-gray-300 space-y-1">
          <p>1. <b>Save to GitHub</b> im Emergent Editor → Push deines Codes</p>
          <p>2. <b>GitHub Actions</b> baut automatisch Windows/macOS/Linux Builds aus <code className="text-cyan-400">obd-agent/</code></p>
          <p>3. Versionsnummer wird in <code className="text-cyan-400">obd-agent/VERSION</code> automatisch hochgezählt (Patch)</p>
          <p>4. <b>Release</b> wird mit allen 3 Plattform-Artefakten erstellt</p>
          <p>5. <b>Agent</b> ruft beim Start <code className="text-cyan-400">/api/dongle-shop/agent/check-update</code> auf → vergleicht Version</p>
          <p>6. Bei neuer Version: Banner mit Download/Auto-Install</p>
        </CardContent>
      </Card>
    </div>
  );
};
